import { ChevronRight, X } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useId } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListRow } from "@/components/ui/list-card";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

/** A value row: label left, current value and a chevron right. */
export const SettingsListRow = ({
  label,
  value,
  onClick
}: {
  label: string;
  value: string;
  onClick: () => void;
}) => (
  <ListRow
    asChild
    title={label}
    control={
      <span className="flex items-center gap-1.5 text-base text-fg-3">
        <span className="max-w-105 truncate">{value}</span>
        <ChevronRight className="size-3.5" />
      </span>
    }
  >
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="w-full"
    />
  </ListRow>
);

export const SettingsField = ({
  label,
  className,
  ...props
}: ComponentProps<typeof Input> & { label: string }) => {
  const id = useId();
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...props} />
    </div>
  );
};

export const EditActions = ({
  saving,
  saveLabel = "Save",
  onCancel,
  onSave
}: {
  saving: boolean;
  saveLabel?: string;
  onCancel: () => void;
  onSave: () => void;
}) => (
  <div className="mt-2 flex w-full justify-end gap-2">
    <Button variant="secondary" size="sm" onClick={onCancel}>
      Cancel
    </Button>
    <Button size="sm" disabled={saving} onClick={onSave}>
      {saving ? <Spinner /> : null}
      {saveLabel}
    </Button>
  </div>
);

/** The edit dialog shared by the Account and Security tabs. */
export const SettingsEditDialog = ({
  open,
  title,
  error,
  onClose,
  children
}: {
  open: boolean;
  title: string;
  error?: string;
  onClose: () => void;
  children: ReactNode;
}) => (
  <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
    <DialogContent
      showCloseButton={false}
      aria-describedby={undefined}
      className="max-h-[calc(100vh-2rem)] gap-0 overflow-y-auto p-0 sm:max-w-140"
    >
      <div className="flex items-center justify-between border-b border-line-1 py-3 pr-3 pl-6">
        <DialogTitle className="text-lg font-medium text-fg">
          {title}
        </DialogTitle>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          onClick={onClose}
        >
          <X />
        </Button>
      </div>
      <div className="flex flex-col gap-3 px-6 pt-4 pb-6">
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        {children}
      </div>
    </DialogContent>
  </Dialog>
);
